import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { WishlistProvider } from "./context/WishlistContext";
import { CompareProvider } from "./context/CompareContext";
import { ProtectedRoute, AdminRoute, BusinessRoute } from "./components/shared/ProtectedRoute";
import Navbar from "./components/layout/Navbar";
import Footer from "./components/layout/Footer";
import WhatsAppButton from "./components/ui/WhatsAppButton";
import CompareBar from "./components/shared/CompareBar";
import PWAInstallPrompt from "./components/ui/PWAInstallPrompt";
import { AdminLayout } from "./pages/AdminDashboard";

// ── Public pages ───────────────────────────────────────────────────────────────
import HomePage            from "./pages/HomePage";
import ShopPage            from "./pages/ShopPage";
import ProductDetailPage   from "./pages/ProductDetailPage";
import CartPage            from "./pages/CartPage";
import ComparePage         from "./pages/ComparePage";
import WishlistPage        from "./pages/WishlistPage";
import NotFoundPage        from "./pages/NotFoundPage";
import AboutPage           from "./pages/AboutPage";
import SolarCalculatorPage from "./pages/SolarCalculatorPage";
import TermsPage           from "./pages/TermsPage";
import PrivacyPage         from "./pages/PrivacyPage";

// ── Auth pages (no layout) ────────────────────────────────────────────────────
import LoginPage           from "./components/auth/LoginPage";
import RegisterPage        from "./components/auth/RegisterPage";

// ── Protected ─────────────────────────────────────────────────────────────────
import CheckoutPage        from "./pages/CheckoutPage";
import OrderSuccessPage    from "./pages/OrderSuccessPage";
import AccountOrdersPage   from "./pages/AccountOrdersPage";
import AccountReferralsPage from "./pages/AccountReferralsPage";
import ReturnRequestPage   from "./pages/ReturnRequestPage";
import ReturnPolicyPage    from "./pages/ReturnPolicyPage";
import { storeIncomingReferral } from "./utils/referrals";

// ── Business ──────────────────────────────────────────────────────────────────
import BusinessSubmitPage    from "./pages/BusinessSubmitPage";
import BusinessDashboardPage from "./pages/BusinessDashboardPage";

// ── Admin ─────────────────────────────────────────────────────────────────────
import AdminDashboard        from "./pages/AdminDashboard";
import AdminProductsPage     from "./pages/AdminProductsPage";
import AdminAddProductPage   from "./pages/AdminAddProductPage";
import AdminOrdersPage       from "./pages/AdminOrdersPage";
import AdminSubmissionsPage  from "./pages/AdminSubmissionsPage";
import AdminUsersPage        from "./pages/AdminUsersPage";
import AdminAnalyticsPage    from "./pages/AdminAnalyticsPage";
import AdminReturnsPage      from "./pages/AdminReturnsPage";
import AdminCouponsPage      from "./pages/AdminCouponsPage";
import AdminShippingPage     from "./pages/AdminShippingPage";
import AdminBannersPage      from "./pages/AdminBannersPage";
import SlimAdBanner          from "./components/ui/SlimAdBanner";

import "./styles/index.css";

// Captures ?ref=... across any landing page and persists it
function ReferralTracker() {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get("ref");
    if (ref) {
      storeIncomingReferral(ref);
    }
  }, [location]);
  return null;
}

// Main storefront layout (Navbar + Footer + floating buttons)
function StoreLayout({ children }) {
  return (
    <>
      <SlimAdBanner placement="global_top" dismissible />
      <Navbar />
      <main className="home-main-container">{children}</main>
      <Footer />
      <WhatsAppButton phone="2349155896484" />
      <CompareBar />
      <PWAInstallPrompt />
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ReferralTracker />
      <AuthProvider>
        <CartProvider>
          <WishlistProvider>
            <CompareProvider>
              <Toaster
                position="top-right"
                toastOptions={{
                  style: { fontFamily: "var(--font-body)", fontSize: 14, borderRadius: 10 },
                  success: { iconTheme: { primary: "var(--success)", secondary: "#fff" } },
                }}
              />

              <Routes>
                {/* ── Public storefront ─────────────────────────────────── */}
                <Route path="/"                   element={<StoreLayout><HomePage /></StoreLayout>} />
                <Route path="/shop"               element={<StoreLayout><ShopPage /></StoreLayout>} />
                <Route path="/product/:id"         element={<StoreLayout><ProductDetailPage /></StoreLayout>} />
                <Route path="/cart"               element={<StoreLayout><CartPage /></StoreLayout>} />
                <Route path="/compare"            element={<StoreLayout><ComparePage /></StoreLayout>} />
                <Route path="/wishlist"           element={<StoreLayout><WishlistPage /></StoreLayout>} />
                <Route path="/about"              element={<StoreLayout><AboutPage /></StoreLayout>} />
                <Route path="/solar-calculator"   element={<StoreLayout><SolarCalculatorPage /></StoreLayout>} />
                <Route path="/solar_calculator.html" element={<StoreLayout><SolarCalculatorPage /></StoreLayout>} />
                <Route path="/terms"              element={<StoreLayout><TermsPage /></StoreLayout>} />
                <Route path="/privacy"            element={<StoreLayout><PrivacyPage /></StoreLayout>} />
                <Route path="/returns"            element={<StoreLayout><ReturnPolicyPage /></StoreLayout>} />

                {/* ── Static HTML compatibility aliases ─────────────────── */}
                <Route path="/index.html" element={<Navigate to="/" replace />} />
                <Route path="/shop.html"  element={<Navigate to="/shop" replace />} />
                <Route path="/about.html" element={<Navigate to="/about" replace />} />
                <Route path="/admin.html" element={<Navigate to="/admin" replace />} />

                {/* ── Auth (no Navbar/Footer) ───────────────────────────── */}
                <Route path="/login"    element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />

                {/* ── Logged-in users ───────────────────────────────────── */}
                <Route path="/checkout" element={
                  <ProtectedRoute><StoreLayout><CheckoutPage /></StoreLayout></ProtectedRoute>
                } />
                <Route path="/order-success/:id" element={
                  <ProtectedRoute><StoreLayout><OrderSuccessPage /></StoreLayout></ProtectedRoute>
                } />
                <Route path="/account/orders" element={
                  <ProtectedRoute><StoreLayout><AccountOrdersPage /></StoreLayout></ProtectedRoute>
                } />
                <Route path="/account/referrals" element={
                  <ProtectedRoute><StoreLayout><AccountReferralsPage /></StoreLayout></ProtectedRoute>
                } />
                <Route path="/account" element={
                  <ProtectedRoute><StoreLayout><AccountOrdersPage /></StoreLayout></ProtectedRoute>
                } />
                <Route path="/request-return" element={
                  <ProtectedRoute><StoreLayout><ReturnRequestPage /></StoreLayout></ProtectedRoute>
                } />

                {/* ── Business sellers ──────────────────────────────────── */}
                <Route path="/business/submit" element={
                  <BusinessRoute><StoreLayout><BusinessSubmitPage /></StoreLayout></BusinessRoute>
                } />
                <Route path="/business/dashboard" element={
                  <BusinessRoute><StoreLayout><BusinessDashboardPage /></StoreLayout></BusinessRoute>
                } />

                {/* ── Admin panel ───────────────────────────────────────── */}
                <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
                  <Route index              element={<AdminDashboard />} />
                  <Route path="products"    element={<AdminProductsPage />} />
                  <Route path="products/add"        element={<AdminAddProductPage />} />
                  <Route path="products/edit/:id"   element={<AdminAddProductPage />} />
                  <Route path="orders"      element={<AdminOrdersPage />} />
                  <Route path="submissions" element={<AdminSubmissionsPage />} />
                  <Route path="users"       element={<AdminUsersPage />} />
                  <Route path="analytics"   element={<AdminAnalyticsPage />} />
                  <Route path="returns"     element={<AdminReturnsPage />} />
                  <Route path="coupons"     element={<AdminCouponsPage />} />
                  <Route path="banners"     element={<AdminBannersPage />} />
                  <Route path="shipping"    element={<AdminShippingPage />} />
                </Route>

                {/* ── 404 ───────────────────────────────────────────────── */}
                <Route path="*" element={<StoreLayout><NotFoundPage /></StoreLayout>} />
              </Routes>
            </CompareProvider>
          </WishlistProvider>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
